import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { Grid, Typography } from '@mui/material';
import MainCard from 'components/Cards/MainCard';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing } from 'utils/constants';
import { getSettings } from 'components/../redux/actions/settings';
import { useNavigate } from 'react-router-dom';

const initialState = {
    batterycap: { data: { values: [], labels: [] }, title: '', orderVal: 0 },
    devicesVtime: { data: { values: [], dates: [] }, title: '', orderVal: 0 },
    devicetypes: {
        data: { os: { values: [], labels: [] }, type: { values: [], labels: [] } },
        title: '',
        orderVal: 0
    },
    devusage: { data: { values: [], labels: [] }, title: '', orderVal: 0 },
    psactivity: {
        data: {
            User_Active: [],
            All_On: [],
            CPU_On: [],
            All_Asleep: [],
            All_Off: [],
            Unknown: [],
            dates: []
        },
        title: '',
        orderVal: 0
    },
    devicedetails: {
        data: { device: { values: [], labels: [] }, system: { values: [], labels: [] } },
        title: '',
        orderVal: 0
    }
};

const Home = () => {
    const theme = useTheme();
    const { userInfo } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const manageOrganizationData = useSelector((state) => state.management.organization?.selected);
    const [dashboardData, setDashboardData] = useState(initialState);
    const [dashboardOrders, setDashboardOrders] = useState([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        getDashboardData();
        dispatch(getSettings(!!userInfo.admin_permissions.org_admin));
    }, [dispatch, userInfo.admin_permissions.org_admin]);

    const getDashboardData = () => {
        ApiHelper.get('/api/reports/dashboard/')
            .then((res) => {
                setDashboardData(res.data.plot_data);
                setDashboardOrders(res.data.orders);
                setLoading(false);
            })
            .catch((err) => {
                if (err?.response?.status !== 401) {
                    toast.warning('Failed to get the plots data!');
                    setLoading(false);
                }
            });
    };
    const seriesOfDevicesOS = dashboardData?.devicetypes?.data.os.values;
    const optionsOfDevicesOS = {
        title: {
            text: 'Devices by OS',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: dashboardData?.devicetypes?.data.os.labels,
        legend: { position: 'bottom' }
    };

    const seriesOfDevicesType = dashboardData?.devicetypes?.data.type.values;
    const optionsOfDevicesType = {
        title: {
            text: 'Devices by Type',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: dashboardData?.devicetypes?.data.type.labels,
        colors: ['#2F3EB1', '#E38B03', '#A303E3'],
        legend: { position: 'bottom' }
    };

    const seriesOfBatteryCharge = [
        {
            name: 'Number of Devices',
            data: dashboardData?.batterycap?.data.values
        }
    ];
    const optionsOfBatteryCharge = {
        chart: {
            toolbar: {
                show: false
            }
        },
        plotOptions: {
            bar: {
                distributed: true
            }
        },
        xaxis: {
            categories: ['0-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'],
            title: {
                text: 'Battery Charge Percentile'
            }
        },
        yaxis: {
            title: {
                text: 'Number of devices'
            }
        },
        dataLabels: {
            enabled: false
        },
        colors: ['#A13426', '#A15D27', '#A19A27', '#26A125', '#25A167', '#259BA1']
    };

    const seriesOfPowerStates = [
        {
            name: 'User Active',
            data: dashboardData?.psactivity?.data.User_Active
        },
        {
            name: 'All On',
            data: dashboardData?.psactivity?.data.All_On
        },
        {
            name: 'CPU On',
            data: dashboardData?.psactivity?.data.CPU_On
        },
        {
            name: 'All Sleep',
            data: dashboardData?.psactivity?.data.All_Asleep
        },
        {
            name: 'All Off',
            data: dashboardData?.psactivity?.data.All_Off
        },
        {
            name: 'Unknown',
            data: dashboardData?.psactivity?.data.Unknown
        }
    ];
    const optionsOfPowerStates = {
        chart: {
            stacked: true,
            toolbar: {
                show: false
            }
        },
        colors: ['#045DFB', '#FB041F', '#F4FB04', '#03F948', '#036705', '#ffffff'],
        xaxis: {
            categories: dashboardData?.psactivity?.data.dates,
            title: {
                text: 'Date'
            }
        },
        yaxis: {
            title: {
                text: '% of Day'
            }
        },
        dataLabels: {
            enabled: false
        }
    };

    const seriesOfDevices = [
        {
            name: 'Devices',
            data: dashboardData?.devicesVtime?.data.values
        }
    ];
    const optionsOfDevices = {
        chart: {
            zoom: {
                enabled: false
            },
            toolbar: {
                show: false
            }
        },
        xaxis: {
            categories: dashboardData?.devicesVtime?.data.labels,
            title: {
                text: 'Date'
            }
        },
        yaxis: {
            title: {
                text: 'Number of devices'
            }
        }
    };

    const seriesOfDevicesUsage = [
        {
            name: 'Number of Devices',
            data: dashboardData?.devusage?.data?.values
        }
    ];
    const optionsOfDevicesUsage = {
        chart: {
            toolbar: {
                show: false
            }
        },
        xaxis: {
            categories: dashboardData?.devusage?.data?.labels,
            title: {
                text: 'Device Usage(Avg. Hours per Day)'
            }
        },
        yaxis: {
            title: {
                text: 'Number of devices'
            }
        },
        dataLabels: {
            enabled: false
        }
    };

    const handleChartClick = (reportType) => {
        switch (reportType) {
            case 'devicetypes':
                navigate('/operational-reports/device-type');
                break;
            case 'devicesVtime':
                navigate('/activity-reports/device-time');
                break;
            case 'psactivity':
                navigate('/activity-reports/powerstate');
                break;
            case 'batterycap':
                navigate('/operational-reports/battery-health');
                break;
            case 'devusage':
                navigate('/activity-reports/device-usage');
                break;
            default:
                break;
        }
    };

    return (
        <MainCard
            cardHeaderShow={true}
            title="WakeSMART Server Management"
            cardHeaderTitle={manageOrganizationData?.orgid ? manageOrganizationData?.name : userInfo.user.orgid.name}
        >
            <Grid container spacing={gridSpacing} sx={{ minHeight: 600 }}>
                {loading === true ? (
                    <Grid item xs={12} sx={{ height: 600 }}>
                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>Please wait...</Typography>
                    </Grid>
                ) : dashboardOrders.length > 0 ? (
                    dashboardOrders.map((data, key) =>
                        data.name === 'devicetypes' ? (
                            <React.Fragment key={key}>
                                {dashboardData?.devicetypes?.data.os.values.length === 0 &&
                                dashboardData?.devicetypes?.data.type.values.length === 0 ? (
                                    <Grid item xs={4}>
                                        <Typography sx={{ fontSize: '16px', fontWeight: '500' }}>Devices by OS and Type</Typography>
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            There is no data
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={2} onClick={() => handleChartClick('devicetypes')} style={{ cursor: 'pointer' }}>
                                            <Chart options={optionsOfDevicesOS} series={seriesOfDevicesOS} type="pie" height={300} />
                                        </Grid>
                                        <Grid item xs={2} onClick={() => handleChartClick('devicetypes')} style={{ cursor: 'pointer' }}>
                                            <Chart options={optionsOfDevicesType} series={seriesOfDevicesType} type="pie" height={300} />
                                        </Grid>
                                    </>
                                )}
                            </React.Fragment>
                        ) : (
                            <Grid
                                item
                                xs={4}
                                key={key}
                                sx={{ minHeight: 300 }}
                                onClick={() => handleChartClick(data.name)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Typography sx={{ fontSize: '16px', fontWeight: '500' }}>{dashboardData[data.name].title}</Typography>
                                {data.name === 'devicesVtime' &&
                                    (dashboardData?.devicesVtime?.data.values.length !== 0 ? (
                                        <Chart options={optionsOfDevices} series={seriesOfDevices} type="line" height={300} />
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            There is no data
                                        </Typography>
                                    ))}
                                {data.name === 'psactivity' &&
                                    (dashboardData?.psactivity?.data.length !== 0 ? (
                                        <Chart options={optionsOfPowerStates} series={seriesOfPowerStates} type="bar" height={300} />
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            There is no data
                                        </Typography>
                                    ))}
                                {data.name === 'batterycap' &&
                                    (dashboardData?.batterycap?.data.values.length !== 0 ? (
                                        <Chart options={optionsOfBatteryCharge} series={seriesOfBatteryCharge} type="bar" height={300} />
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            There is no data
                                        </Typography>
                                    ))}
                                {data.name === 'devusage' &&
                                    (dashboardData?.devusage?.data.length !== 0 ? (
                                        <Chart options={optionsOfDevicesUsage} series={seriesOfDevicesUsage} type="bar" height={300} />
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            There is no data
                                        </Typography>
                                    ))}
                            </Grid>
                        )
                    )
                ) : (
                    <Grid item xs={12} sx={{ height: 600 }}>
                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                            There is no plot data
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </MainCard>
    );
};

export default Home;
